import DatetimeInput from '@cohort/merchants/components/form/v2/DatetimeInput';
import Input from '@cohort/merchants/components/form/v2/Input';
import ListInput from '@cohort/merchants/components/form/v2/ListInput';
import SelectInput from '@cohort/merchants/components/form/v2/SelectInput';
import type {DynamicCohortRuleType} from '@cohort/merchants/pages/users/cohort/rule/utils';
import type {DataType} from '@cohort/shared/schema/common/rules/dataTypes';
import {useEffect} from 'react';
import type {Control, Path, UseFormRegister} from 'react-hook-form';
import {useController} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {match} from 'ts-pattern';

type CriteriaInputValueProps = {
  dataType: DataType;
  name: Path<DynamicCohortRuleType>;
  control: Control<DynamicCohortRuleType>;
  register: UseFormRegister<DynamicCohortRuleType>;
};

const CriteriaInputValue: React.FC<CriteriaInputValueProps> = ({
  dataType,
  register,
  control,
  name,
}) => {
  const {t} = useTranslation('pages', {
    keyPrefix: 'users.cohort.rule.criteria.criteriaInputValue',
  });
  const {field} = useController({
    name,
    control,
  });

  useEffect(() => {
    if (field.value === undefined || field.value === null) {
      if (dataType === 'string_list') {
        field.onChange([]);
      } else {
        field.onChange('');
      }
    }
  }, [dataType, field]);

  return match(dataType)
    .with('boolean', () => (
      <SelectInput
        name={name}
        options={[
          {value: true, label: t('true')},
          {value: false, label: t('false')},
        ]}
        register={register}
        control={control}
      />
    ))
    .with('date', () => (
      <DatetimeInput variant="date" control={control} name={name} register={register} />
    ))
    .with('null', () => null)
    .with('string_list', () => <ListInput name={name} control={control} register={register} />)
    .with('number', () => (
      <Input
        type="number"
        name={name}
        placeholder={t('placeholder')}
        register={register}
        control={control}
      />
    ))
    .with('string', () => (
      <Input name={name} placeholder={t('placeholder')} register={register} control={control} />
    ))
    .exhaustive();
};

export default CriteriaInputValue;
